import { Columns03, CursorBox, Edit01, FilterLines, Package, Plus, SearchLg, Trash01, XClose } from '@untitled-ui/icons-react/build/cjs';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AutoCompleteGoogle from 'src/components/AutoCompleteGoogle/AutoCompleteGoogle';
import BulkActionBar from 'src/components/BulkActionBar';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import CustomPagination from 'src/components/CustomPagination';
import DeleteModal from 'src/components/DeleteModal';
import DraggableLayout from 'src/components/DraggableLayout';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TableCmp from 'src/components/TableCmp';
import FilterLayout from 'src/components/TableFilter/FilterLayout';
import TableTabButton from 'src/components/TableTabButton';
import CreateViewModal from 'src/components/TableTabButton/CreateViewModal';
import TooltipCmp from 'src/components/TooltipCmp';
import { ORDER_STATUS } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { useAddressDetails } from 'src/hooks/useAddressDetails';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { setTrackColumns } from 'src/redux/basic.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { updateTrackTableColumnSettings } from 'src/services/RadarService';

import { TRACK_FILTER_SERVICE_ARR } from './__mocks__/track.mock';
import useGoogleMapsScript from './hooks/useGoogleMapsScript';
import useTrackViews from './hooks/useTrackViews';
import TrackFilterModal from './modals/TrackFilterModal';
import TrackTableLoaderRow from './skeletons/TrackTableLoaderRow';
import { TRACK_DATE_PRESET, TRACK_FILTER_FIELD_CLASS, TRACK_FILTER_SEARCH_FIELD_CLASS, TRACK_SORTABLE_INITIAL_STATE, TRACK_TABLE_ALIAS } from './track.const';
import { makeTrackEmptyStateTitleDesc } from './track.emptyState';
import { formatTrackRowData, getActiveTrackDatePreset, getTrackActionColumnWidthClass, isTrackParamsChanged, transformTrackColumnsToHeadCells } from './track.helpers';
import { ITrackParams } from './track.interface';
import { setTrackParams, trackList as trackListSelector, trackLoading, trackParams as trackParamsSelector, trackTotalRecords as trackTotalRecordsSelector } from './track.slice';
import { normalizeTrackSearchQuery } from './track.utils';
import TrackDateRangeQuickPicker from './TrackDateRangeQuickPicker';
import TrackDraggableCell from './TrackDraggableCell';
import TrackTableRow from './TrackTableRow';

const recordsPerPageArray = [
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

const TrackTableView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const trackList = useAppSelector(trackListSelector);
    const totalRecords = useAppSelector(trackTotalRecordsSelector);
    const isLoading = useAppSelector(trackLoading);
    const trackParams = useAppSelector(trackParamsSelector);
    const currentUser = useAppSelector(currentUserSelector);
    // Timezone lives on basic.appearance, not on auth — reading state.auth.timezone silently
    // yields undefined and falls back to UTC, which shifts "today" a day for western zones.
    const userTimezone = useAppSelector((state) => state.basic.appearance?.timezone) || 'UTC';
    const activeTab = trackParams.status;

    // Saved views: tabs = stage tabs + the user's saved views; all CRUD / apply / dirty-check
    // logic lives in the hook so the map view's Overview card shares it (see useTrackViews).
    const {
        tabs,
        viewNames,
        activeViewTab,
        activeCustomView,
        isDefaultTab,
        isCreateViewEnable,
        isSaveEnable,
        isTabsLoading,
        isViewChangeLoading,
        isError,
        setIsError,
        viewAction,
        viewModalText,
        applyView,
        applyDefaultTab,
        revertToBaseline,
        applyModalFilters,
        openCreateView,
        openRenameView,
        openDeleteView,
        closeViewAction,
        saveActiveView,
        handleViewModalSave,
        handleDeleteModalClose,
    } = useTrackViews();
    // Rename / Edit / Delete dropdown on the active saved-view tab (second click toggles it).
    const [isShowAction, setIsShowAction] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Inline Shipper / Consignee / Origin / Destination — the same sources the filter modal
    // uses, so the table row and the modal never disagree on options.
    const { shipperOptions, consigneeOptions, isShipperLoading, handleSearchShipper, handleSearchConsignee } = useAddressDetails();
    const { isReady: isMapScript, isLoading: isScriptLoading } = useGoogleMapsScript();

    const [searchInput, setSearchInput] = useState(trackParams.search ?? '');

    useEffect(() => {
        const nextSearch = trackParams.search ?? '';
        setSearchInput((current) => (normalizeTrackSearchQuery(current) === nextSearch ? current : nextSearch));
    }, [trackParams.search]);

    // `stage` remembers the stage that was active when the filter bar opened so Cancel can
    // restore it even if the Stage select moved it meanwhile (same as WAL / Orders).
    const [isFilterView, setIsFilterView] = useState<{ isShow: boolean; stage: string | null }>({ isShow: false, stage: null });
    // State captured when the filter bar OPENS. Cancel restores exactly this — matching WAL, where
    // Cancel undoes only what you changed inside the bar. Reverting to the view baseline instead
    // would also throw away filters that were already applied before you opened it.
    const filterPanelSnapshotRef = useRef<{ params: ITrackParams; search: string; refSearch: string } | null>(null);

    // "Filters applied but not saved to this view" — the same signal the Save View button uses
    // (isSaveEnable), gated exactly like WAL's useUnsavedViewFilters: silent while the filter bar
    // is open (you're mid-edit), while tabs are still loading, and while the active tab isn't in
    // the list yet (a deleted view leaves activeViewTab dangling for a render).
    const hasUnsavedFilters = useMemo(
        () => !isFilterView.isShow && !isTabsLoading && tabs.some((tab) => tab.value === activeViewTab) && isSaveEnable,
        [isFilterView.isShow, isTabsLoading, tabs, activeViewTab, isSaveEnable]
    );

    // REF/PRO#/BOL is typed, so it echoes each keystroke locally and commits to params (which
    // reloads the list) debounced — the SelectBox filters commit immediately.
    const [refSearchInput, setRefSearchInput] = useState(trackParams.refSearch ?? '');

    useEffect(() => {
        setRefSearchInput(trackParams.refSearch ?? '');
    }, [trackParams.refSearch]);

    // Bulk selection state
    const [bulkSelection, setBulkSelection] = useState<{ isOpen: boolean; data: number[] }>({ isOpen: false, data: [] });
    const selectedTrackIds = bulkSelection.data;
    const [showAllSelected, setShowAllSelected] = useState(false);

    const setSelectedTrackIds = useCallback((ids: number[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelectTrack = useCallback((id: number) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const displayedTracks = useMemo(
        // Selection is keyed by legDbId — masterOrderId repeats across the split rows of a
        // Show-as-Multi-Leg order, legDbId is unique in both grains.
        () => (showAllSelected ? trackList.filter((order: any) => selectedTrackIds.includes(order.legDbId)) : trackList),
        [showAllSelected, trackList, selectedTrackIds]
    );

    // Sized to the action icons a row renders (priority + optional bell) — applied to the action th, row td, and skeleton so they stay aligned.
    const isTextNotificationFeatureVisible = useAppSelector((state: any) => state.track.portalVisibility?.textNotification !== false);
    const actionColumnWidthClass = useMemo(() => getTrackActionColumnWidthClass(displayedTracks, isTextNotificationFeatureVisible), [displayedTracks, isTextNotificationFeatureVisible]);

    // Column management state — loaded at app boot via /common/init (see AppLayout)
    const trackColumns = useAppSelector((state: any) => state.basic.trackColumns);
    const [isRowSortable, setIsRowSortable] = useState(TRACK_SORTABLE_INITIAL_STATE);
    const [columnsOrder, setColumnsOrder] = useState<any[]>([]);

    const divisionOptions = useMemo(() => (currentUser?.divisions ?? []).map((div: any) => ({ value: div.id, label: div.displayName })), [currentUser?.divisions]);

    const billingAccountOptions = useMemo(() => {
        const divisions: any[] = currentUser?.divisions ?? [];
        const source = trackParams.divisionId ? divisions.filter((div: any) => div.id === trackParams.divisionId) : divisions;

        return source.flatMap((div: any) =>
            (div.customerBillingAccount ?? []).map((acc: any) => ({
                value: acc.id,
                label: acc.displayName,
            }))
        );
    }, [currentUser?.divisions, trackParams.divisionId]);

    const params = useMemo(
        () => ({ page: trackParams.page, limit: trackParams.limit, sortField: trackParams.sortField, sortType: trackParams.sortType }),
        [trackParams.page, trackParams.limit, trackParams.sortField, trackParams.sortType]
    );

    const setParams = useCallback(
        (newParams: any) => {
            const resolved = typeof newParams === 'function' ? newParams(params) : newParams;
            dispatch(setTrackParams(resolved));
        },
        [dispatch, params]
    );

    const isAllSelected = trackList.length > 0 && selectedTrackIds.length === trackList.length;

    // Build headCells dynamically from trackColumns
    const headCells = useMemo(() => {
        const hasData = trackList.length > 0 && !isLoading;
        const dynamicCells = transformTrackColumnsToHeadCells(trackColumns, activeTab, hasData, t);

        const checkboxCell = {
            id: 'checkbox',
            name: (
                <CheckBox
                    id="selectAllTracks"
                    checked={isAllSelected}
                    onChangeFunc={() => setSelectedTrackIds(isAllSelected ? [] : trackList.map((order: any) => order.legDbId))}
                    name="selectAllTracks"
                    value="selectAllTracks"
                    disabled={isLoading}
                />
            ),
            sortable: false,
            visible: true,
            // Explicit width so the th holds 40px even when the body is empty/loading (no rows to size the column)
            rowClassName: 'w-[40px] min-w-[40px] max-w-[40px] !pr-2 sticky left-0 bg-inherit',
        };

        const actionCell = {
            id: 'action',
            name: '',
            sortable: false,
            visible: true,
            // Width matches the computed body/skeleton action width so header and rows stay aligned;
            // fall back to the row/skeleton default so the th never collapses on an empty list.
            rowClassName: `sticky right-0 bg-inherit ${actionColumnWidthClass || 'min-w-[64px] max-w-[64px] w-[64px]'}`,
        };

        return [checkboxCell, ...dynamicCells, actionCell];
    }, [trackColumns, activeTab, trackList, isLoading, t, isAllSelected, setSelectedTrackIds, actionColumnWidthClass]);

    // Column rearrangement handlers
    const handleSortable =
        (isOpen: boolean = true) =>
        () => {
            if (isOpen) {
                const formattedData = formatTrackRowData(trackList.length ? trackList : [], activeTab, t);

                if (formattedData.length > 0) {
                    const sorted = trackColumns
                        .map((column: any) => {
                            const found = formattedData[0].find((f: any) => f.id === column.id);

                            return found ? { ...found, visible: column.visible } : null;
                        })
                        .filter(Boolean);
                    setIsRowSortable({ isOpen: true, data: [sorted] });
                }
            } else {
                setIsRowSortable(TRACK_SORTABLE_INITIAL_STATE);
                setColumnsOrder([]);
            }
        };

    const handleColumnChange = (columns: any) => {
        setColumnsOrder(columns);
    };

    const hasColumnChanges = useMemo(() => isTrackParamsChanged(columnsOrder, trackColumns), [columnsOrder, trackColumns]);

    const handleColumnOrderSubmit = async () => {
        const cleanedColumns = columnsOrder.map((column: any) => {
            // eslint-disable-next-line no-unused-vars
            const { data: data, boxWrapClassName: boxWrapClassName, ...rest } = column;

            return rest;
        });
        dispatch(setTrackColumns(cleanedColumns));
        setIsRowSortable(TRACK_SORTABLE_INITIAL_STATE);
        setColumnsOrder([]);

        updateTrackTableColumnSettings({
            tableAlias: TRACK_TABLE_ALIAS,
            columnSettings: cleanedColumns,
        }).catch(() => {
            // Silent fail - local state already updated
        });
    };

    const dispatchSearch = useCallback(
        debounce((value: string) => dispatch(setTrackParams({ search: value, page: 1 })), 300),
        []
    );

    const handleSearchChange = (e: any) => {
        const value = e?.target?.value ?? '';
        const query = normalizeTrackSearchQuery(value);
        setSearchInput(value);

        if (query || value === '') {
            dispatchSearch(query);
        }
    };

    const handleClearSearch = () => {
        setSearchInput('');
        dispatchSearch('');
    };

    const dispatchRefSearch = useCallback(
        debounce((value: string) => dispatch(setTrackParams({ refSearch: value, page: 1 })), 400),
        []
    );

    const handleRefSearchChange = (e: any) => {
        const value = e?.target?.value ?? '';
        setRefSearchInput(value);
        dispatchRefSearch(value);
    };

    // AutoCompleteGoogle fires onChangeFunc with a place object on selection, and again with
    // a DOM event on blur — only a real place (fullAddress) or an explicit clear commits.
    const handleAutocompleteChange = (field: 'originSearch' | 'destinationSearch') => (value: any) => {
        if (!value || value.type === 'blur' || (value.target && value.type)) return;

        if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
            dispatch(setTrackParams({ [field]: value.fullAddress || '', page: 1 }));
        }
    };

    const handleAddressInputChange = (field: 'originSearch' | 'destinationSearch') => (value: string) => {
        // Clearing the text (x button / backspace to empty) drops the filter right away.
        if (!value) dispatch(setTrackParams({ [field]: '', page: 1 }));
    };

    const handleServiceTypeChange = (e: any) => {
        dispatch(setTrackParams({ serviceType: e?.value ?? 'all', page: 1 }));
    };

    const handleDivisionChange = (e: any) => {
        dispatch(setTrackParams({ divisionId: e?.value ?? null, billingAccountId: null, page: 1 }));
    };

    const handleBillingAccountChange = (e: any) => {
        dispatch(setTrackParams({ billingAccountId: e?.value ?? null, page: 1 }));
    };

    // Chip border state: dashed while the filter sits on its default (today), solid once the
    // user picks any other range — mirrors the `filter-selectbox-with-value` treatment on the
    // sibling SelectBox filters. Derived from the dates, like the label and arrow stride.
    const isDefaultDateRange = getActiveTrackDatePreset(trackParams, userTimezone) === TRACK_DATE_PRESET.today;

    // Same builder the map sidebar uses, so both views show identical WAL copy for every stage.
    // Keyed on the COMMITTED `trackParams.search`, not the local `searchInput`: WAL reads
    // `isParams?.search`, and the local box is ahead of the debounce, so keying on it flips the
    // copy to "No orders match your search criteria" while the list still holds the old results.
    const emptyState = useMemo(() => makeTrackEmptyStateTitleDesc(trackParams, !!trackParams.search, userTimezone, t), [trackParams, userTimezone, t]);

    // Open the filter bar / Cancel. Filters apply live as they're changed (same as WAL) — Cancel
    // closes the bar and rewinds to the snapshot taken when it opened; Apply just closes it,
    // keeping what's applied.
    const handleFilterView = (isShow: boolean) => () => {
        setIsShowAction(false);

        if (isShow) {
            filterPanelSnapshotRef.current = { params: trackParams, search: searchInput, refSearch: refSearchInput };
            setIsFilterView({ isShow: true, stage: isDefaultTab ? activeViewTab : null });

            return;
        }

        const snapshot = filterPanelSnapshotRef.current;
        filterPanelSnapshotRef.current = null;
        setIsFilterView({ isShow: false, stage: null });

        if (!snapshot) return;

        // Drop any keystroke still in flight, or it lands after the rewind and re-applies itself.
        dispatchSearch.cancel();
        dispatchRefSearch.cancel();
        setSearchInput(snapshot.search);
        setRefSearchInput(snapshot.refSearch);
        // setTrackParams no-ops when nothing differs, so an untouched bar costs no refetch.
        dispatch(setTrackParams(snapshot.params));
    };

    // Apply: close the bar and keep the filters. They are already applied to the list — the button
    // exists to dismiss the bar without the rewind Cancel does. Same as WAL's handleFilterApply.
    const handleFilterApply = () => {
        setIsShowAction(false);
        filterPanelSnapshotRef.current = null;
        setIsFilterView({ isShow: false, stage: null });
    };

    // Save View: a saved view is active → overwrite it in place; a stage tab is active →
    // open Create New View (name modal), the view is created from the current params.
    const handleViewSave = async () => {
        if (activeCustomView) {
            const saved = await saveActiveView();

            if (saved) setIsFilterView({ isShow: false, stage: null });

            return;
        }

        openCreateView();
    };

    // The create modal closes the bar itself once the view exists (the new view is now the
    // baseline, so there is nothing left to save).
    const handleViewModalSaveAndClose = async (data: any) => {
        const saved = await handleViewModalSave(data);

        if (saved && !data?.rename) setIsFilterView({ isShow: false, stage: null });
    };

    const handleTabClick = (tab: any) => () => {
        // Second click on the active saved view toggles its Rename / Edit / Delete menu.
        if (tab?.isIconShow && tab.value === activeViewTab) {
            setIsShowAction((prev) => !prev);

            return;
        }

        setIsShowAction(false);

        if (tab?.isIconShow) {
            applyView(tab);
        } else {
            applyDefaultTab(tab?.value);
        }
    };

    const VIEW_ACTIONS = useMemo(
        () => [
            {
                icon: Edit01,
                label: t('QuotingHub.QuotingDashboard.Rename_View'),
                onClick: () => {
                    setIsShowAction(false);
                    openRenameView();
                },
            },
            {
                icon: CursorBox,
                label: t('QuotingHub.QuotingDashboard.Edit_View'),
                onClick: () => {
                    setIsShowAction(false);
                    setIsFilterView({ isShow: true, stage: null });
                },
            },
            {
                icon: Trash01,
                label: t('QuotingHub.QuotingDashboard.Delete_View'),
                onClick: () => {
                    setIsShowAction(false);
                    openDeleteView();
                },
                isDestructive: true,
            },
        ],
        [t, openRenameView, openDeleteView]
    );

    // Same badge rule as the map sidebar: dates / stage / division / billing account are
    // inline controls, the rest count as "advanced" filters set through the modal.
    const advancedFilterCount = [trackParams.refSearch, trackParams.shipperId, trackParams.consigneeId, trackParams.originSearch, trackParams.destinationSearch].filter(Boolean).length;

    const handleRowClick = (row: any) => {
        if (row?.masterOrderId) {
            // orderStatus seeds the detail page's stage switcher on first paint, before
            // the detail API resolves — see navStateOrderStatus in RadarOrderDetail.
            navigate(`${PATH.TRACK}/${row.masterOrderId}`, { state: { orderStatus: row?.orderStatus } });
        }
    };

    return (
        <div className="table-box-wrap w-full relative overflow-hidden">
            <div className={`transition-all duration-300 ${isFilterView.isShow ? 'h-[90px]' : 'h-[45px]'}`}>
                {isFilterView.isShow ? (
                    <FilterLayout
                        // On a stage tab the button opens Create New View, so hide it once the view cap is hit.
                        isSaveBtn={isDefaultTab ? isCreateViewEnable : true}
                        onSave={handleViewSave}
                        onApply={handleFilterApply}
                        isSaveEnable={isSaveEnable}
                        isLoading={isViewChangeLoading}
                        onCancel={handleFilterView(false)}
                        search={searchInput}
                        handleSearchChange={handleSearchChange}
                    >
                        <>
                            <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto scrollbar-hide">
                                {/* REF/PRO#/BOL — dashed border when empty, solid when filled, matching the
                                    SelectBox filter fields (ported from WAL RadarViewFilter). */}
                                <InputText
                                    inputName="refSearch"
                                    size="sm"
                                    placeholder={t('Radar.Ref_Pro_Bol')}
                                    className={`form_control pr-7 hover:border-grayText ${refSearchInput ? 'border-solid' : 'border-dashed'}`}
                                    parentClassName={TRACK_FILTER_FIELD_CLASS}
                                    value={refSearchInput}
                                    isClearable={true}
                                    onChangeFunc={handleRefSearchChange}
                                />
                                <SelectBox
                                    name="shipperId"
                                    isShowTooltipOnTruncate
                                    size="sm"
                                    placeholder={t('Radar.Select_Shipper')}
                                    className="form_control"
                                    parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${TRACK_FILTER_SEARCH_FIELD_CLASS} ${
                                        trackParams.shipperId ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                    }`}
                                    SelectDropdownWrapClass="filter-select-dropdown-sm"
                                    options={shipperOptions}
                                    isClearable={true}
                                    isSearchable={true}
                                    isOptionLoading={isShipperLoading}
                                    onInputChange={handleSearchShipper}
                                    value={shipperOptions?.find((opt: any) => String(opt.value) === String(trackParams.shipperId)) ?? null}
                                    onChangeFunc={(e: any) => dispatch(setTrackParams({ shipperId: e?.value ?? null, page: 1 }))}
                                />
                                <SelectBox
                                    name="consigneeId"
                                    isShowTooltipOnTruncate
                                    size="sm"
                                    placeholder={t('Radar.Select_Consignee')}
                                    className="form_control"
                                    parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${TRACK_FILTER_SEARCH_FIELD_CLASS} ${
                                        trackParams.consigneeId ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                    }`}
                                    SelectDropdownWrapClass="filter-select-dropdown-sm"
                                    options={consigneeOptions}
                                    isClearable={true}
                                    isSearchable={true}
                                    isOptionLoading={isShipperLoading}
                                    onInputChange={handleSearchConsignee}
                                    value={consigneeOptions?.find((opt: any) => String(opt.value) === String(trackParams.consigneeId)) ?? null}
                                    onChangeFunc={(e: any) => dispatch(setTrackParams({ consigneeId: e?.value ?? null, page: 1 }))}
                                />
                                <SelectBox
                                    parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${TRACK_FILTER_FIELD_CLASS} ${
                                        trackParams.serviceType && trackParams.serviceType !== 'all' ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                    }`}
                                    SelectDropdownWrapClass="filter-select-dropdown-sm"
                                    name="serviceType"
                                    isShowTooltipOnTruncate
                                    className="form_control"
                                    options={TRACK_FILTER_SERVICE_ARR}
                                    placeholder="All Services"
                                    isClearable={true}
                                    isSearchable={false}
                                    value={TRACK_FILTER_SERVICE_ARR.find((o) => o.value === trackParams.serviceType) ?? null}
                                    onChangeFunc={handleServiceTypeChange}
                                />
                                <SelectBox
                                    parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${TRACK_FILTER_FIELD_CLASS} ${
                                        trackParams.divisionId ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                    }`}
                                    SelectDropdownWrapClass="filter-select-dropdown-sm"
                                    name="division"
                                    isShowTooltipOnTruncate
                                    className="form_control"
                                    options={divisionOptions}
                                    placeholder="Division"
                                    isClearable={true}
                                    isSearchable={false}
                                    value={divisionOptions.find((o) => o.value === trackParams.divisionId) ?? null}
                                    onChangeFunc={handleDivisionChange}
                                />
                                <SelectBox
                                    parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${TRACK_FILTER_FIELD_CLASS} ${
                                        trackParams.billingAccountId ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                    }`}
                                    SelectDropdownWrapClass="filter-select-dropdown-sm"
                                    name="billingAccount"
                                    isShowTooltipOnTruncate
                                    className="form_control"
                                    options={billingAccountOptions}
                                    placeholder="Billing Account"
                                    isClearable={true}
                                    isSearchable={false}
                                    isDisabled={!trackParams.divisionId}
                                    value={billingAccountOptions.find((o) => o.value === trackParams.billingAccountId) ?? null}
                                    onChangeFunc={handleBillingAccountChange}
                                />
                                {/* Quick date-range control (WAL radar port) — chip border matches the
                                    sibling filter selects: dashed while on the default (today), solid
                                    once any other range is picked. Disabled on In Transit, where the
                                    backend ignores the date filter entirely. */}
                                <TrackDateRangeQuickPicker
                                    disabled={trackParams.status === ORDER_STATUS.IN_TRANSIT}
                                    wrapClassName={`h-[28px] flex-none rounded-lg border px-2 shadow-sm bg-white hover:border-grayText ${
                                        isDefaultDateRange ? 'border-dashed border-borderPrimary' : 'border-solid border-borderPrimary'
                                    }`}
                                    // This filter bar scrolls horizontally, and popper treats that short row as the
                                    // preset menu's overflow boundary — so it clamps the menu back up over the chip
                                    // instead of dropping it below like the sibling SelectBox filters. Push it clear
                                    // of the row here, overriding the shared -10px, so only this instance is affected.
                                    dropdownWrapClassName="!mt-[43px]"
                                />

                                {/* Origin + Destination (Google Places) — rendered once the script is ready;
                                    dashed when empty, solid when filled, like the selects. */}
                                {isMapScript && (
                                    <>
                                        <AutoCompleteGoogle
                                            inputName="originSearch"
                                            mapParentClass={TRACK_FILTER_FIELD_CLASS}
                                            placeholder={t('Radar.From_City_Or_State')}
                                            className={`form_control !text-xs pr-6 truncate !py-[5px] !h-[28px] hover:border-grayText ${trackParams.originSearch ? 'border-solid' : 'border-dashed'}`}
                                            value={trackParams.originSearch ?? ''}
                                            isStrictCityStateOnly
                                            isClearable
                                            disabled={isScriptLoading}
                                            onChangeFunc={handleAutocompleteChange('originSearch')}
                                            onAddressInputChange={handleAddressInputChange('originSearch')}
                                            onBlur={handleAutocompleteChange('originSearch')}
                                        />
                                        <AutoCompleteGoogle
                                            inputName="destinationSearch"
                                            mapParentClass={TRACK_FILTER_FIELD_CLASS}
                                            placeholder={t('Radar.To_City_Or_State')}
                                            className={`form_control !text-xs pr-6 truncate !py-[5px] !h-[28px] hover:border-grayText ${
                                                trackParams.destinationSearch ? 'border-solid' : 'border-dashed'
                                            }`}
                                            value={trackParams.destinationSearch ?? ''}
                                            isStrictCityStateOnly
                                            isClearable
                                            disabled={isScriptLoading}
                                            onChangeFunc={handleAutocompleteChange('destinationSearch')}
                                            onAddressInputChange={handleAddressInputChange('destinationSearch')}
                                            onBlur={handleAutocompleteChange('destinationSearch')}
                                        />
                                    </>
                                )}
                            </div>

                            {/* Advance Filter modal — same entry point and badge as the map sidebar. */}
                            <TooltipCmp message={t('Radar.Advance_Filter')} childrenClassName="flex items-center flex-none">
                                <ButtonCmp size="square-sm" className="btn_secondary_black relative flex-none" icon={<Plus className="w-4 h-4" />} onClick={() => setIsFilterModalOpen(true)}>
                                    <>
                                        {advancedFilterCount > 0 && (
                                            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-danger500 text-white text-[10px] leading-4 font-semibold text-center">
                                                {advancedFilterCount}
                                            </span>
                                        )}
                                    </>
                                </ButtonCmp>
                            </TooltipCmp>
                        </>
                    </FilterLayout>
                ) : (
                    <div className="table-bottom-header table-header-filter-space !gap-x-2 items-center !border-b border-utilityGray200 !flex-nowrap">
                        <div className={`table-header-bottom-left flex gap-x-1 flex-1 scrollbar-hide ${isRowSortable.isOpen ? 'opacity-60 pointer-events-none !cursor-default' : ''}`}>
                            <div className="flex items-center gap-x-1">
                                <TableTabButton
                                    tabArray={tabs}
                                    tabParentClassName="whitespace-nowrap scrollbar-hide"
                                    isActive={activeViewTab}
                                    isTabDataInClick={true}
                                    isShowAction={isShowAction}
                                    tabTextClass="!max-w-[104px] truncate"
                                    handleOnClick={handleTabClick}
                                    handleOutSideClick={() => setIsShowAction(false)}
                                    isSkeletonLoading={isTabsLoading}
                                    skeletonCount={3}
                                    viewActions={VIEW_ACTIONS}
                                />
                                {isCreateViewEnable && !isTabsLoading && (
                                    <div className="cursor-pointer p-[5px] text-textSecondary text-xs leading-[1.5] font-semibold rounded-lg hover:bg-utilityGray100" onClick={openCreateView}>
                                        <TooltipCmp message={t('QuotingHub.QuotingDashboard.Create_View')}>
                                            <Plus className="w-4 h-4 flex-none" />
                                        </TooltipCmp>
                                    </div>
                                )}
                            </div>
                        </div>

                        {!isRowSortable.isOpen ? (
                            <>
                                {/* Escape hatch for filters applied on top of a view: one click back to the
                                    view's saved state (or the stage defaults). Mirrors WAL radar's TableView. */}
                                {hasUnsavedFilters && (
                                    <ButtonCmp
                                        className="btn-link-outline-primary !px-0 !py-0 flex-none"
                                        onClick={() => revertToBaseline()}
                                        icon={<XClose className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />}
                                    >
                                        {t('Common.Clear_Filters')}
                                    </ButtonCmp>
                                )}
                                {/* Active state + dot so unsaved filters are visible without opening the bar —
                                    otherwise a filtered table looks identical to an unfiltered one. */}
                                <ButtonCmp
                                    className={`${hasUnsavedFilters ? 'relative btn-outline-primary !bg-primary50' : 'btn_secondary_black'} flex-none !px-2 !py-[5px] !gap-1`}
                                    onClick={handleFilterView(true)}
                                >
                                    <SearchLg className="w-4 h-4" />
                                    <FilterLines className="w-4 h-4" />
                                    {hasUnsavedFilters && <p className="border-[3px] border-white bg-primary500 rounded-full w-3 h-3 absolute -top-1.5 -right-1.5" />}
                                </ButtonCmp>

                                <ButtonCmp
                                    className="btn_secondary_black flex-none"
                                    onClick={handleSortable()}
                                    size="square-sm"
                                    disabled={trackList.length === 0}
                                    icon={<Columns03 className="w-4 h-4" />}
                                >
                                    <></>
                                </ButtonCmp>

                                <InputText
                                    inputName="searchTrack"
                                    placeholder="Search"
                                    className="bg-white focus:bg-white pl-8 pr-7 placeholder:text-gray500 shadow-sm font-normal search-input"
                                    icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                                    value={searchInput}
                                    inputType="text"
                                    onChangeFunc={handleSearchChange}
                                    parentClassName="table-searchInput max-mdm:flex-1 max-smd:flex-[0_0_100%] hidden flex-none"
                                    isClearable={true}
                                />

                                <div className="table-recordsPerPage flex-none">
                                    <SelectBox
                                        SelectDropdownWrapClass="table-recordsPerPageDropdown"
                                        selectLabelWrapClass="table-recordsPerPageSelectValue"
                                        name="recordsPerPageGroup"
                                        id="recordsPerPageGroup"
                                        className="form_control shadow"
                                        size="sm"
                                        options={recordsPerPageArray}
                                        onChangeFunc={(event: any) => {
                                            if (trackParams.limit !== event.value) {
                                                dispatch(setTrackParams({ limit: event.value, page: 1 }));
                                            }
                                        }}
                                        isSearchable={false}
                                        value={recordsPerPageArray.find((val) => val.value === trackParams.limit)}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <ButtonCmp type="button" onClick={handleSortable(false)} className="btn_secondary_black">
                                    {t('Common.Cancel')}
                                </ButtonCmp>
                                <ButtonCmp type="button" onClick={handleColumnOrderSubmit} disabled={!hasColumnChanges} className="btn-outline-primary">
                                    {t('Common.Save')}
                                </ButtonCmp>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="flex-1 w-full">
                <div className={`draggable-layout-view ${isRowSortable.isOpen ? 'slide-in' : 'slide-out'}`}>
                    <DraggableLayout sortableData={isRowSortable?.data} renderCellContent={TrackDraggableCell} onColumnChange={handleColumnChange} />
                </div>

                <div className={`w-full table-box-container draggable-layout-view ${isRowSortable.isOpen ? 'slide-out pointer-events-none' : 'slide-in'}`}>
                    {bulkSelection.isOpen && (
                        <BulkActionBar
                            selectedRows={selectedTrackIds}
                            setSelectedRows={setSelectedTrackIds}
                            allIds={trackList.map((order: any) => order.legDbId)}
                            showOnlySelected={showAllSelected}
                            onShowOnlySelectedChange={setShowAllSelected}
                        />
                    )}

                    <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                        <TableCmp
                            headCells={headCells}
                            tableDataArr={isLoading ? [] : displayedTracks}
                            TableRowCmp={TrackTableRow}
                            TableLoaderRowCmp={TrackTableLoaderRow}
                            tableRowCmpProps={{ activeTab, headCells, selectedTrackIds, onToggleSelect: handleToggleSelectTrack, actionColumnWidthClass }}
                            params={params}
                            setParams={setParams}
                            tableRowHeight="xs"
                            tableBodyRowClass="bg-white"
                            isTableDataLoading={isLoading}
                            numberOfSkeletonRows={20}
                            isTableRowClickable={true}
                            onRowClick={handleRowClick}
                            isBulkBarOpen={bulkSelection.isOpen}
                            isFirstColumnSticky
                            isActionColumnSticky
                        />
                    </div>

                    {!isLoading && !trackList.length && (
                        <NotFoundUI
                            title={emptyState.title}
                            desc={emptyState.desc}
                            containerClassName="min-h-[calc(100%_-_172.5px)] !h-auto"
                            iconClassName="!hidden"
                            iconSecondaryClassName="!flex"
                            iconSecondary={trackParams.search ? <SearchLg className="w-5 h-5 text-primary" /> : <Package className="w-5 h-5 text-primary" />}
                            handleActionType2={trackParams.search ? handleClearSearch : undefined}
                            buttonText2={trackParams.search ? 'Clear Search' : false}
                            showButtonIcon2={false}
                        />
                    )}

                    {/* Pager lives INSIDE the table card, like WAL Radar's TableView — outside it sat below
                        the card in a non-scrolling parent and was never visible. totalRecords is the
                        server total (the list is paged server-side), otherwise Next is always disabled. */}
                    {!isLoading && trackList.length > 0 && (
                        <CustomPagination
                            recordsPerPage={trackParams.limit}
                            totalRecords={totalRecords || trackList.length}
                            currentPage={trackParams.page}
                            handlePagination={(page: number) => dispatch(setTrackParams({ page }))}
                        />
                    )}
                </div>
            </div>

            {isFilterModalOpen && (
                <TrackFilterModal
                    handleClose={() => setIsFilterModalOpen(false)}
                    onApply={applyModalFilters}
                    defaultValues={{
                        refSearch: trackParams.refSearch ?? '',
                        shipperId: trackParams.shipperId ?? null,
                        consigneeId: trackParams.consigneeId ?? null,
                        status: trackParams.status,
                        serviceType: trackParams.serviceType && trackParams.serviceType !== 'all' ? trackParams.serviceType : null,
                        startDate: trackParams.startDate || null,
                        endDate: trackParams.endDate || null,
                        originSearch: trackParams.originSearch ?? '',
                        destinationSearch: trackParams.destinationSearch ?? '',
                    }}
                />
            )}

            {(viewAction.create_view || viewAction.rename_view) && (
                <CreateViewModal
                    title={viewModalText.title}
                    primaryBtnText={viewModalText.primaryBtnText}
                    handleClose={closeViewAction}
                    handleSave={handleViewModalSaveAndClose}
                    isLoading={isViewChangeLoading}
                    viewNames={viewNames}
                    modalData={viewAction.data}
                    isError={isError}
                    setIsError={setIsError}
                />
            )}

            {viewAction.delete_view && (
                <DeleteModal
                    moduleTitle={t('QuotingHub.QuotingDashboard.Delete_View')}
                    moduleDeleteModalContent={t('QuotingHub.QuotingDashboard.Delete_View_desc', { View_name: viewAction.data?.view_name })}
                    handleClose={handleDeleteModalClose}
                    dangerBtnText={t('Common.Delete')}
                    isDeleteLoading={isViewChangeLoading}
                />
            )}
        </div>
    );
};

export default TrackTableView;
